"use client";

import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import "./Page.css";
import { TagList } from "../TagList";
import MarioLoader from "../MarioLoader";
import AuthorInfor from "./AuthorInfor";
import { CopyBlock, dracula } from "react-code-blocks";
import ScrollToTop from "@/app/components/ui/common/ScrollToTopForBlog";
import { useRouter } from "next/navigation";

// Fetch blog content from Strapi API
export const getBlogContent = async ({ slug }) => {
    try {
        const res = await fetch(
            `${process.env.NEXT_PUBLIC_STRAPI_URL}/api/blogs?filters[slug][$eq]=${slug}&populate[0]=seo.metaImage&populate[1]=seo.metaArticle&populate[2]=seo.metaArticle.authors&populate[3]=image&populate[4]=tags&populate[5]=seo.metaArticle.authors.image&populate[6]=blogs&populate[7]=prev&populate[8]=next&populate[9]=blogs.titleImage&populate[10]=blogs.seo.metaDescription`,
            {
                headers: {
                    Authorization: `Bearer ${process.env.NEXT_PUBLIC_SUPER_TOKEN}`,
                },
            }
        );

        if (!res.ok) {
            throw new Error(`Failed to fetch blog content: ${res.statusText}`);
        }

        const data = await res.json();
        return data;
    } catch (err) {
        console.error(err);
        return null;
    }
};

const FilteredBlog = ({ params }) => {
    const slug = params.slug;

    const [content, setContent] = useState(null);
    const tags = content?.data[0]?.attributes?.tags;

    const [parsedContent, setParsedContent] = useState({
        sections: [],
        keyPoints: [],
        keyPointIds: [],
    });

    const [activeKeyPoint, setActiveKeyPoint] = useState("section-0"); // Automatically updated by scroll
    const [clickedKeyPoint, setClickedKeyPoint] = useState(null); // Track manually clicked key point
    const sectionRefs = useRef({});

    const router = useRouter();
    const [isNavigating, setIsNavigating] = useState(false);

    useEffect(() => {
        const handleStart = () => setIsNavigating(true);
        const handleComplete = () => setIsNavigating(false);

        router.events?.on("routeChangeStart", handleStart);
        router.events?.on("routeChangeComplete", handleComplete);
        router.events?.on("routeChangeError", handleComplete);

        return () => {
            router.events?.off("routeChangeStart", handleStart);
            router.events?.off("routeChangeComplete", handleComplete);
            router.events?.off("routeChangeError", handleComplete);
        };
    }, [router]);


    useEffect(() => {
        async function fetchContent() {
            const fetchedContent = await getBlogContent({ slug: params.slug });
            console.log("🚀 ~ fetchContent ~ fetchedContent:", fetchedContent)

            setContent(fetchedContent);
        }
        fetchContent();
    }, [params.slug]);

    const parseBlogContent = (content) => {
        const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^\)]+)\)/g;
        const imageRegex = /!\[.*?\]\s*\((\/uploads\/[^\)]+)\)/g;
        const boldTextRegex = /\*\*(.*?)\*\*/g;
        const blockquoteRegex = /^>\s*(.*)/;
        const checklistRegex = /- \[ \]\s*(.*)/; // Matches the "- [ ]" pattern

        const sections = [];
        const keyPoints = [];
        const keyPointIds = [];
        let sectionIndex = 0;

        const lines = content.split("\n");
        const renderedHeadings = new Set(); // Track all headings to avoid duplicates

        let lineIndex = 0;

        while (lineIndex < lines.length) {
            const line = lines[lineIndex];

            // Handle H2 headings
            if (line.startsWith("## ") && !line.startsWith("###")) {
                const rawHeading = line.replace(/^##\s*/, "").trim();

                // Extract leading numbers (if present)
                const numberMatch = rawHeading.match(/^\d+\./); // Matches "1.", "2.", etc.
                const leadingNumber = numberMatch ? numberMatch[0] : ""; // Use the found number or leave empty

                // Extract link, if any
                const linkMatch = rawHeading.match(linkRegex);
                const linkText = linkMatch
                    ? linkMatch[1]
                    : rawHeading.replace(numberMatch, "").trim(); // Text without numbers and links

                const headingContent = (
                    <>
                        {linkMatch ? (
                            <>
                                {rawHeading.split(linkMatch[0])[0]}
                                <a
                                    href={linkMatch[2]}
                                    target="_blank"
                                    style={{
                                        color: "#410EAD",
                                        // color: "#7b3fe4",
                                        textDecoration: "none",
                                        fontWeight: "700",
                                    }}
                                >
                                    {linkMatch[1]}
                                </a>
                                {rawHeading.split(linkMatch[0])[1]}{" "}
                            </>
                        ) : (
                            rawHeading // Use the full heading including numbers
                        )}
                    </>
                );

                if (!renderedHeadings.has(rawHeading)) {
                    sections.push({
                        content: (
                            <h2
                                key={`heading-${sectionIndex}`}
                                className="text-[26px] font-bold text-[#242424] mt-[50px]"
                            >
                                {headingContent}
                            </h2>
                        ),
                        id: `section-${sectionIndex}`,
                    });

                    // Always add the heading to key points
                    keyPoints.push(leadingNumber + linkText.trim());
                    keyPointIds.push(`section-${sectionIndex}`);

                    sectionIndex++;
                    renderedHeadings.add(rawHeading);
                }
                lineIndex++;
                continue;
            }

            // Handle H3 headings
            if (line.startsWith("### ") && !line.startsWith("####")) {
                const subHeading = line.replace(/^###\s*/, "").trim();
                if (!renderedHeadings.has(subHeading)) {
                    sections.push({
                        content: (
                            <h3
                                key={`subheading-${sectionIndex}`}
                                className="text-2xl font-semibold text-[#242424] my-2 tracking-wide"
                            >
                                {subHeading}
                            </h3>
                        ),
                        id: `section-${sectionIndex}`,
                    });
                    sectionIndex++;
                    renderedHeadings.add(subHeading);
                }
                lineIndex++;
                continue; // Skip further processing of this line
            }

            // Handle H4 headings
            if (line.startsWith("#### ") && !line.startsWith("#####")) {
                const subHeading = line.replace(/^####\s*/, "").trim();
                if (!renderedHeadings.has(subHeading)) {
                    sections.push({
                        content: (
                            <h4
                                key={`subheading-${sectionIndex}`}
                                className="text-[21px] font-semibold text-[#242424] my-1 tracking-wide"
                            >
                                {subHeading}
                            </h4>
                        ),
                        id: `section-${sectionIndex}`,
                    });
                    sectionIndex++;
                    renderedHeadings.add(subHeading);
                }
                lineIndex++;
                continue; // Skip further processing of this line
            }

            // Handle H5 headings
            if (line.startsWith("##### ")) {
                const subHeading = line.replace(/^#####\s*/, "").trim();
                if (!renderedHeadings.has(subHeading)) {
                    sections.push({
                        content: (
                            <h5
                                key={`subheading-${sectionIndex}`}
                                className="text-lg font-semibold text-[#242424] my-0.5 heading-h5"
                            >
                                {subHeading}
                            </h5>
                        ),
                        id: `section-${sectionIndex}`,
                    });
                    sectionIndex++;
                    renderedHeadings.add(subHeading);
                }
                lineIndex++;
                continue; // Skip further processing of this line
            }

            // Handle checklist items ("- [ ]" pattern)
            const checklistMatch = line.match(checklistRegex);
            if (checklistMatch) {
                let checklistContent = checklistMatch[1].trim();

                // Replace links
                checklistContent = checklistContent.replace(
                    linkRegex,
                    (match, p1, p2) =>
                        `<a href="${p2}" target="_blank" style="color: #410EAD; text-decoration: none; font-weight: 700;">${p1}</a>`
                    // `<a href="${p2}" target="_blank" style="color: #7b3fe4; text-decoration: none; font-weight: 700;">${p1}</a>`
                );

                // Replace bold text
                checklistContent = checklistContent.replace(
                    boldTextRegex,
                    (match, p1) =>
                        `<span style="font-weight: bold; color: #242424; overflow-wrap: break-word;">${p1}</span>`
                );

                sections.push({
                    content: (
                        <div
                            key={`checklist-${sectionIndex}`}
                            className="flex items-baseline mb-3 break-words"
                        >
                            <span className="inline-flex w-1.5 h-1.5 bg-[#242424] rounded-full mr-2 flex-shrink-0"></span>
                            <p
                                className="text-[17px] break-words tracking-wider !text-[#242424]"
                                dangerouslySetInnerHTML={{
                                    __html: checklistContent,
                                }}
                            />
                        </div>
                    ),
                    id: `section-${sectionIndex}`,
                });

                sectionIndex++;
                lineIndex++;
                continue;
            }

            // Handle code blocks
            if (line.startsWith("```")) {
                let codeContent = "";
                const language = "solidity"; // Default language for code blocks
                lineIndex++;
                while (
                    lineIndex < lines.length &&
                    !lines[lineIndex].startsWith("```")
                ) {
                    codeContent += `${lines[lineIndex]}\n`;
                    lineIndex++;
                }
                codeContent = codeContent.trim();
                lineIndex++; // Skip closing ```
                sections.push({
                    content: (
                        <div
                            key={`code-${sectionIndex}`}
                            // className="my-4 p-4 bg-gray-100 rounded-md shadow-md max-w-full overflow-auto"
                            className="max-w-full overflow-auto"
                        >
                            <div className="code-block-container bg-gray-50 rounded-lg border border-gray-300 p-4 shadow-sm mx-auto max-w-4xl overflow-auto">
                                <CopyBlock
                                    text={codeContent}
                                    language={language}
                                    theme={dracula}
                                    showLineNumbers={false}
                                    wrapLines={true}
                                    customStyle={{ overflow: "auto" }}
                                />
                            </div>
                        </div>
                    ),
                    id: `section-${sectionIndex}`,
                });
                sectionIndex++;
                continue;
            }

            // Handle blockquotes
            if (blockquoteRegex.test(line)) {
                const blockquoteText = line.match(blockquoteRegex)[1];
                sections.push({
                    content: (
                        <blockquote
                            key={`blockquote-${sectionIndex}`}
                            className="p-4 border-l-8 border-[#410ead] bg-[#e9ddff] text-xl rounded-xl shadow-xl text-[#242424] my-4"
                        >
                            {blockquoteText}
                        </blockquote>
                    ),
                    id: `section-${sectionIndex}`,
                });
                sectionIndex++;
                lineIndex++;
                continue;
            }

            // Handle images
            let imageMatch;
            while ((imageMatch = imageRegex.exec(line)) !== null) {
                const imageUrl = imageMatch[1];
                sections.push({
                    content: (
                        <div
                            key={`image-${sectionIndex}`}
                            className="relative w-full my-6"
                        >
                            <Image
                                src={`${process.env.NEXT_PUBLIC_STRAPI_URL}${imageUrl}`}
                                alt={`Image ${sectionIndex}`}
                                width={750}
                                height={600}
                                layout="intrinsic"
                                className="object-fill mx-auto"
                            />
                        </div>
                    ),
                    id: `section-${sectionIndex}`,
                });
                sectionIndex++;
            }

            // Skip lines that match the raw image markdown
            if (imageRegex.test(line)) {
                lineIndex++;
                continue;
            }

            // Handle regular text, links, and bold text
            let formattedLine = line;
            formattedLine = formattedLine.replace(
                linkRegex,
                (match, p1, p2) =>
                    `<a href="${p2}" target="_blank" style="color: #410EAD; text-decoration: none; font-weight: 700;">${p1}</a>`
            );
            formattedLine = formattedLine.replace(
                boldTextRegex,
                (match, p1) =>
                    `<span style="font-weight: bold; color: #242424; overflow-wrap: break-word; word-wrap: break-word;">${p1}</span>`
            );

            if (formattedLine.trim()) {
                sections.push({
                    content: (
                        <p
                            key={`paragraph-${sectionIndex}`}
                            className="mt-5 text-[17px] text-[#242424] break-words tracking-wide"
                            dangerouslySetInnerHTML={{ __html: formattedLine }}
                        />
                    ),
                    id: `section-${sectionIndex}`,
                });
                sectionIndex++;
            }

            lineIndex++;
        }

        return { sections, keyPoints, keyPointIds };
    };

    useEffect(() => {
        if (content) {
            const blogContent = content?.data[0]?.attributes?.content || "";
            const parsed = parseBlogContent(blogContent);
            setParsedContent(parsed);
        }
    }, [content]);

    const scrollToSection = (id) => {
        if (sectionRefs.current[id]) {
            const sectionElement = sectionRefs.current[id];
            const topOffset = 100; // Adjust this value according to your needs

            const elementPosition =
                sectionElement.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementPosition - topOffset;

            window.scrollTo({
                top: offsetPosition,
                behavior: "smooth",
            });

            setActiveKeyPoint(id); // Set active when clicked
            setClickedKeyPoint(id); // Set clicked key point as red
        }
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveKeyPoint(entry.target.id);
                    }
                });
            },
            {
                threshold: 0.5,
                rootMargin: "0px 0px -50% 0px",
            }
        );

        Object.values(sectionRefs.current).forEach((section) => {
            if (section) {
                observer.observe(section);
            }
        });

        return () => {
            Object.values(sectionRefs.current).forEach((section) => {
                if (section) {
                    observer.unobserve(section);
                }
            });
        };
    }, [parsedContent.sections]);


    if (isNavigating || !content) {
        return (
            <div  className="fixed overflow-hidden min-w-screen min-h-screen inset-0 bg-white/[70%] z-[9999] flex items-center justify-center">
                <ScrollToTop /> 
                <MarioLoader />
            </div>
        );
    }

    const { sections, keyPoints, keyPointIds } = parsedContent;

    const getKeyPointClass = (index) => {
        const currentKeyPointIndex = keyPointIds.indexOf(activeKeyPoint);
        const isClicked = keyPointIds[index] === clickedKeyPoint;
        return isClicked
            ? "font-bold text-[#410EAD]"
            : index <= currentKeyPointIndex;
    };

    return (
        <>
            <div className="flex flex-col items-center w-full min-h-screen py-6 ">
                <div className="w-full h-full">
                    {/* Main content with improved spacing */}
                    <div className="w-full space-y-5 text-md">
                        <div className="">
                            {sections.map((section, index) => (
                                <div
                                    key={index}
                                    id={section.id}
                                    ref={(el) =>
                                        (sectionRefs.current[section.id] = el)
                                    }
                                    className={`mb-3 ${activeKeyPoint === section.id
                                        ? "bg-white"
                                        : ""
                                        }`}
                                >
                                    {section.content}
                                </div>
                            ))}
                        </div>

                        {/* Author information */}
                        <div className="px-6 md:px-8 lg:px-12">
                            <AuthorInfor content={content} />

                            <TagList
                                classes="md:place-content-start mt-4"
                                tags={tags}
                            />

                            <div className="flex justify-between w-full text-sm !mt-6 font-semibold">
                                <Link
                                    href={`${process.env.NEXT_PUBLIC_BLOG_URL}/blog/${content?.data[0]?.attributes?.prev?.data?.attributes?.slug}`}
                                    className="border-b border-b-black hover:text-regal-purple hover:border-b-regal-purple"
                                >
                                    PREV BLOG
                                </Link>
                                <Link
                                    href={`${process.env.NEXT_PUBLIC_BLOG_URL}/blog/${content?.data[0]?.attributes?.next?.data?.attributes?.slug}`}
                                    className="border-b border-b-black hover:text-regal-purple hover:border-b-regal-purple"
                                >
                                    NEXT BLOG
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default FilteredBlog;
